import { useEffect, useState } from 'react';
import { Card, Row, Col, Input, Avatar, Typography, Button, Space, Empty, Spin, Pagination, Tag } from 'antd';
import { UserOutlined, MailOutlined, PhoneOutlined, SearchOutlined, EyeOutlined } from '@ant-design/icons';
import AdminSidebar from '../Components/Sidebar';
import { getUsersList } from '../redux/slices/adminSlice';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash.debounce';

const { Title, Text } = Typography;

function UserList() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { userList, isLoading } = useSelector((state) => state?.admin);

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 12,
    });
    const [search, setSearch] = useState("");

    useEffect(() => {
        fetchUsers();
    }, [pagination, search]);

    const fetchUsers = async () => {
        const data = {
            page: pagination.current,
            limit: pagination.pageSize,
        };
        if (search.trim() !== "") {
            data.search = search;
        }
        await dispatch(getUsersList(data));
    };

    const handleSearch = debounce((value) => {
        setPagination((prev) => ({ ...prev, current: 1 }));
        setSearch(value);
    }, 500);

    const handlePageChange = (page, pageSize) => {
        setPagination({
            current: page,
            pageSize: pageSize,
        });
    };

    const viewData = (id) => {
        navigate(`/admin/user/${id}`);
    };

    const getInitials = (firstName, lastName) => {
        const first = firstName?.charAt(0) || '';
        const last = lastName?.charAt(0) || '';
        return `${first}${last}`.toUpperCase();
    };

    const getTagColor = (userType) => {
        switch (userType?.toLowerCase()) {
            case 'admin':
                return 'red';
            case 'provider':
                return 'blue';
            case 'user':
                return 'green';
            default:
                return 'default';
        }
    };

    const users = userList?.data || [];
    const total = userList?.total || 0;

    return (
        <>
            <AdminSidebar renderContent={
                <div>
                    <Card
                        title={
                            <Title level={3} style={{ margin: 0 }}>
                                <UserOutlined style={{ marginRight: 8, color: '#CA9AE8' }} />
                                Users
                            </Title>
                        }
                        style={{
                            marginBottom: 24,
                            borderRadius: '12px',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                            border: 'none',
                        }}
                        extra={
                            <Space>
                                <Input.Search
                                    placeholder="Search users..."
                                    prefix={<SearchOutlined />}
                                    onChange={(e) => handleSearch(e.target.value)}
                                    allowClear
                                    style={{ width: 300 }}
                                />
                            </Space>
                        }
                    >
                        {isLoading ? (
                            <div style={{ textAlign: 'center', padding: '60px 0' }}>
                                <Spin size="large" />
                            </div>
                        ) : users.length === 0 ? (
                            <Empty description="No users found" />
                        ) : (
                            <>
                                <Row gutter={[16, 16]}>
                                    {users.map((user) => (
                                        <Col xs={24} sm={12} md={8} lg={6} key={user.key || user.id}>
                                            <Card
                                                hoverable
                                                style={{
                                                    borderRadius: '12px',
                                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                                                    border: 'none',
                                                    height: '100%',
                                                    transition: 'all 0.3s ease',
                                                }}
                                                bodyStyle={{
                                                    padding: '20px',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    textAlign: 'center',
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.transform = 'translateY(-4px)';
                                                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.15)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.transform = 'translateY(0)';
                                                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                                                }}
                                            >
                                                <Avatar
                                                    size={64}
                                                    style={{
                                                        backgroundColor: '#CA9AE8',
                                                        fontSize: '24px',
                                                        fontWeight: 'bold',
                                                        marginBottom: '16px',
                                                    }}
                                                    icon={<UserOutlined />}
                                                >
                                                    {getInitials(user.first_name, user.last_name)}
                                                </Avatar>
                                                <Title level={5} style={{ margin: '0 0 8px 0', color: '#262626' }}>
                                                    {user.first_name} {user.last_name}
                                                </Title>
                                                <Tag color={getTagColor(user.user_type)} style={{ marginBottom: '12px' }}>
                                                    {user.user_type || 'User'}
                                                </Tag>
                                                <div style={{ width: '100%', marginTop: 'auto' }}>
                                                    <Space direction="vertical" size="small" style={{ width: '100%', marginBottom: '16px' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', color: '#8c8c8c' }}>
                                                            <MailOutlined />
                                                            <Text ellipsis style={{ fontSize: '12px', maxWidth: '100%' }}>
                                                                {user.email || 'N/A'}
                                                            </Text>
                                                        </div>
                                                        {user.phone && (
                                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', color: '#8c8c8c' }}>
                                                                <PhoneOutlined />
                                                                <Text style={{ fontSize: '12px' }}>
                                                                    {user.phone}
                                                                </Text>
                                                            </div>
                                                        )}
                                                    </Space>
                                                    <Button
                                                        type="primary"
                                                        icon={<EyeOutlined />}
                                                        onClick={() => viewData(user.key || user.id)}
                                                        style={{
                                                            width: '100%',
                                                            backgroundColor: '#CA9AE8',
                                                            borderColor: '#CA9AE8',
                                                            borderRadius: '6px',
                                                        }}
                                                    >
                                                        View Details
                                                    </Button>
                                                </div>
                                            </Card>
                                        </Col>
                                    ))}
                                </Row>
                                {total > pagination.pageSize && (
                                    <div style={{ marginTop: '24px', textAlign: 'center' }}>
                                        <Pagination
                                            current={pagination.current}
                                            pageSize={pagination.pageSize}
                                            total={total}
                                            onChange={handlePageChange}
                                            onShowSizeChange={handlePageChange}
                                            showSizeChanger
                                            showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} users`}
                                            pageSizeOptions={['12', '24', '48', '96']}
                                        />
                                    </div>
                                )}
                            </>
                        )}
                    </Card>
                </div>
            } />
        </>
    );
}

export default UserList;
